import { useState, useEffect } from "react";
import { Contact } from "@/types/contacts";

import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowRight, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { LastUpdateInfo } from "@/components/dashboard/LastUpdateInfo";
import { ContactStatsCards } from "@/components/dashboard/ContactStatsCards";
import { ContactRegionalChart } from "@/components/dashboard/charts/ContactRegionalChart";
import { ContactStateChart } from "@/components/dashboard/charts/ContactStateChart";
import { EntityTypeChart } from "@/components/dashboard/charts/EntityTypeChart";
import { ContactFilterPanel } from "@/components/dashboard/ContactFilterPanel";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const Index = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Filter states
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [selectedState, setSelectedState] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [searchName, setSearchName] = useState<string>("");

  useEffect(() => {
    const loadContacts = async () => {
      console.log("🔄 [INDEX] Carregando contatos do banco...");
      const startTime = Date.now();
      
      try {
        const { data, error } = await supabase
          .from('contacts')
          .select('*')
          .order('id');

        if (error) {
          console.error("❌ [INDEX] Erro ao buscar contatos:", error);
          throw error;
        }

        console.log(`📦 [INDEX] ${data?.length || 0} contatos carregados`);

        const mappedContacts: Contact[] = data?.map(item => ({
          id: item.id,
          company: item.company || '',
          firstName: item.first_name || '',
          lastName: item.last_name || '',
          position: item.position || '',
          cellPhone: item.cell_phone || '',
          phone: item.phone || '',
          email: item.email || '',
          website: item.website || '',
          city: item.city || '',
          state: item.state || '',
          entityType: item.entity_type as 'restaurant' | 'operator' | 'consolidator',
          region: item.region || '',
          source: item.source as 'manual' | 'form' | 'csv',
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          postalCode: item.postal_code || '',
          country: item.country || 'Brasil',
          addressLine1: item.address_line_1 || '',
          addressLine2: item.address_line_2 || '',
          externalId: item.external_id || '',
          anonymousId: item.anonymous_id || '',
        })) || [];

        setContacts(mappedContacts);

        const loadTime = Date.now() - startTime;
        console.log(`✅ [INDEX] Contatos carregados em ${loadTime}ms`);
        setLoading(false);
      } catch (error) {
        const loadTime = Date.now() - startTime;
        console.error(`❌ [INDEX] Erro ao carregar após ${loadTime}ms:`, error);
        setLoading(false);
        toast({
          title: "Erro ao carregar dados",
          description: error instanceof Error ? error.message : "Não foi possível carregar os contatos.",
          variant: "destructive"
        });
      }
    };

    loadContacts();
  }, [toast]);

  // Filtering logic
  const filterContacts = () => {
    let filtered = [...contacts];

    // Search by name
    if (searchName.trim()) {
      const searchLower = searchName.toLowerCase();
      filtered = filtered.filter((c) => 
        (c.firstName || '').toLowerCase().includes(searchLower) ||
        (c.lastName || '').toLowerCase().includes(searchLower) ||
        (c.company || '').toLowerCase().includes(searchLower)
      );
    }

    // Filter by region
    if (selectedRegion !== "all") {
      filtered = filtered.filter((c) => c.region === selectedRegion);
    }

    // Filter by state
    if (selectedState !== "all") {
      filtered = filtered.filter((c) => c.state === selectedState);
    }

    // Filter by type
    if (selectedType !== "all") {
      if (selectedType === "rsvp") {
        filtered = filtered.filter((c) => c.source === "form");
      } else {
        filtered = filtered.filter((c) => c.entityType === selectedType);
      }
    }

    // Filter by company
    if (selectedCompany !== "all") {
      filtered = filtered.filter((c) => c.company === selectedCompany);
    }

    setFilteredContacts(filtered);
  };

  useEffect(() => {
    filterContacts();
  }, [contacts, selectedRegion, selectedState, selectedType, selectedCompany, searchName]);

  // Active filters counter
  const activeFilters = [
    selectedRegion !== "all",
    selectedState !== "all",
    selectedType !== "all",
    selectedCompany !== "all",
    searchName.trim() !== ""
  ].filter(Boolean).length;

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedRegion("all");
    setSelectedState("all");
    setSelectedType("all");
    setSelectedCompany("all");
    setSearchName("");
  };

  // Dynamic title
  const getDashboardTitle = () => {
    if (selectedType !== "all") {
      const typeLabels: Record<string, string> = {
        restaurant: "Restaurantes",
        operator: "Operadoras",
        consolidator: "Consolidadoras",
        rsvp: "RSVP"
      };
      return `Dashboard de Contatos - ${typeLabels[selectedType] || selectedType}`;
    }
    if (selectedRegion !== "all") {
      return `Dashboard de Contatos - ${selectedRegion}`;
    }
    if (selectedState !== "all") {
      return `Dashboard de Contatos - ${selectedState}`;
    }
    return "Dashboard de Contatos";
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto" />
          <div className="space-y-2">
            <p className="text-lg font-medium">Carregando contatos...</p>
            <p className="text-sm text-muted-foreground">Buscando dados da base</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar onClearData={undefined} onClearContacts={undefined} />
        
        <main className="flex-1">
          <div className="container mx-auto px-4 py-8">
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-foreground">{getDashboardTitle()}</h2>
                <p className="text-muted-foreground">
                  {activeFilters > 0 ? "Análise segmentada dos dados" : "Visão geral de todos os contatos cadastrados"}
                </p>
              </div>

              <ContactFilterPanel
                contacts={contacts}
                selectedRegion={selectedRegion}
                selectedState={selectedState}
                selectedType={selectedType}
                selectedCompany={selectedCompany}
                searchName={searchName}
                onRegionChange={setSelectedRegion}
                onStateChange={setSelectedState}
                onTypeChange={setSelectedType}
                onCompanyChange={setSelectedCompany}
                onSearchNameChange={setSearchName}
              />

              {activeFilters > 0 && (
                <div className="flex items-center justify-between bg-muted/50 p-3 rounded-lg border border-border/50">
                  <p className="text-sm text-muted-foreground">
                    {activeFilters} filtro(s) ativo(s) • Mostrando {filteredContacts.length} de {contacts.length} contatos
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearAllFilters}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Limpar Filtros
                  </Button>
                </div>
              )}

              <ContactStatsCards contacts={filteredContacts} />

              <div className="grid gap-6 md:grid-cols-2">
                <ContactRegionalChart contacts={filteredContacts} />
                <ContactStateChart contacts={filteredContacts} />
                <EntityTypeChart contacts={filteredContacts} />
              </div>

              <Button 
                onClick={() => {
                  const params = new URLSearchParams();
                  
                  if (selectedRegion !== "all") params.set("region", selectedRegion);
                  if (selectedState !== "all") params.set("state", selectedState);
                  if (selectedType !== "all") params.set("type", selectedType);
                  if (selectedCompany !== "all") params.set("company", selectedCompany);
                  if (searchName.trim()) params.set("search", searchName.trim());
                  
                  const queryString = params.toString();
                  navigate(`/contacts${queryString ? `?${queryString}` : ''}`);
                }}
                size="lg"
                className="w-full"
              >
                {activeFilters > 0 ? `Ver ${filteredContacts.length} Contatos Filtrados` : "Ver Todos os Contatos"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <LastUpdateInfo tableName="contacts" entityType={null} />
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Index;
